/**
 * Decides whether the app renders or the sign-in screen does (`SignIn.dc.html`, §8.1).
 *
 * Three states, and all three are real:
 *   · **bypass** — local development, nothing to sign in to;
 *   · **signed out** — show the sign-in screen, which is a normal first load;
 *   · **signed in** — hand the access token to the API client and render the app.
 *
 * The token is pushed into `api.ts` via `setAccessToken` before any child renders, so no
 * component has to know that authentication exists.
 */

import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { setAccessToken } from '../api.js';
import {
  acquireApiToken,
  apiScope,
  currentAccount,
  devBypass,
  entraConfigured,
  initialiseMsal,
  signIn,
} from './msal.js';

type Phase = 'starting' | 'signed-out' | 'ready' | 'misconfigured' | 'failed';

const PANEL: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--bg)',
  padding: 24,
};

const CARD: CSSProperties = {
  width: 392,
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
};

const NOTE: CSSProperties = { fontSize: 11.5, color: 'var(--mu)', lineHeight: 1.55 };

/** Re-acquire a little before the hour so a long audit session never hits a 401. */
const REFRESH_INTERVAL_MS = 45 * 60 * 1000;

/**
 * The AADSTS codes worth translating, because each has exactly one fix and the raw text
 * buries it. Anything else is shown verbatim rather than guessed at.
 */
const AADSTS: Record<string, string> = {
  AADSTS650053:
    'The app registration does not expose the scope being requested. Under "Expose an API", add a scope (the usual name is access_as_user), then set VITE_ENTRA_API_SCOPE to api://<client-id>/<scope-name>.',
  AADSTS500011:
    'Entra cannot find an API to issue this token for. The registration needs an Application ID URI under "Expose an API" — usually api://<client-id> — and at least one scope on it.',
  AADSTS65001:
    'Nobody has consented to this scope yet. Grant admin consent on the registration, or sign in once and accept the prompt.',
  AADSTS9002326:
    'The redirect URI is registered as type "Web" rather than "Single-page application". PKCE only works on a SPA-type URI.',
  AADSTS500207:
    'The app is requesting a token for itself, so the resource must be the bare client id — not api://<client-id>. Set VITE_ENTRA_API_SCOPE to <client-id>/.default (or <client-id>/access_as_user).',
  AADSTS700016: 'The client id is not a registered application in this tenant.',
  AADSTS50011: 'This origin is not a registered redirect URI on the app registration.',
};

/**
 * MSAL errors hide the useful part. `errorCode` is MSAL's own name for the failure and
 * `errorMessage` carries the AADSTS code that actually tells you what to change.
 */
function describeAuthError(error: unknown): string {
  if (typeof error !== 'object' || error === null) return String(error);

  const candidate = error as { errorCode?: string; errorMessage?: string; message?: string };
  const raw = candidate.errorMessage ?? candidate.message ?? String(error);

  const code = /AADSTS\d+/.exec(raw)?.[0];
  const explanation = code === undefined ? undefined : AADSTS[code];

  return [
    candidate.errorCode === undefined ? null : `[${candidate.errorCode}]`,
    explanation ?? raw,
    explanation === undefined || code === undefined ? null : `\n\nEntra said: ${code}`,
  ]
    .filter((part): part is string => part !== null)
    .join(' ');
}

export function AuthGate({ children }: { readonly children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>(devBypass ? 'ready' : 'starting');
  const [problem, setProblem] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      await initialiseMsal();
      const token = await acquireApiToken();

      if (token === null) {
        // Either nobody is signed in, or a redirect is already under way.
        setPhase(currentAccount() === null ? 'signed-out' : 'starting');
        return;
      }

      setAccessToken(token);
      setPhase('ready');
    } catch (error) {
      setProblem(describeAuthError(error));
      setPhase('failed');
    }
  }, []);

  useEffect(() => {
    if (devBypass) {
      // The API is signing every request in as AUTH_DEV_BYPASS_USER; sending no token is
      // correct here, not an omission.
      setAccessToken(null);
      return;
    }
    if (!entraConfigured) {
      setPhase('misconfigured');
      return;
    }
    void load();
  }, [load]);

  // Keep the token fresh for the length of a real audit session.
  useEffect(() => {
    if (devBypass || phase !== 'ready') return;
    const timer = setInterval(() => {
      void acquireApiToken()
        .then((token) => {
          if (token !== null) setAccessToken(token);
        })
        .catch(() => {
          // A failed refresh is not fatal while the current token is still valid; the
          // next request that 401s will send the user back through sign-in.
        });
    }, REFRESH_INTERVAL_MS);
    return () => {
      clearInterval(timer);
    };
  }, [phase]);

  if (phase === 'ready') return <>{children}</>;

  if (phase === 'starting') {
    return (
      <div style={PANEL}>
        <span style={NOTE}>Signing you in…</span>
      </div>
    );
  }

  if (phase === 'misconfigured') {
    return (
      <div style={PANEL}>
        <div style={CARD}>
          <h1 style={{ margin: 0, font: '600 20px/1.2 var(--font-sans)' }}>
            Sign-in is not configured
          </h1>
          <p style={NOTE}>
            <code>VITE_ENTRA_TENANT_ID</code> and <code>VITE_ENTRA_CLIENT_ID</code> are not set,
            and the local bypass is off — so there is no way to authenticate. Set them, or set{' '}
            <code>VITE_AUTH_DEV_BYPASS=true</code> for local development.
          </p>
        </div>
      </div>
    );
  }

  if (phase === 'failed') {
    return (
      <div style={PANEL}>
        <div style={CARD}>
          <h1 style={{ margin: 0, font: '600 20px/1.2 var(--font-sans)' }}>Sign-in failed</h1>
          <p style={{ ...NOTE, color: 'var(--ba)', whiteSpace: 'pre-wrap' }}>{problem}</p>
          <div style={{ ...NOTE, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span>
              Requested scope: <code>{apiScope}</code>
            </span>
            <span>
              Origin: <code>{window.location.origin}</code>
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              setPhase('starting');
              void load();
            }}
            style={{
              height: 40,
              borderRadius: 'var(--r-md)',
              border: '1px solid var(--bd)',
              background: 'var(--sf)',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // signed-out — the sign-in screen from `SignIn.dc.html`, reduced to what it needs.
  return (
    <div style={PANEL}>
      <div style={CARD}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 7,
              background: 'var(--ac)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: '0 0 30px',
            }}
          >
            <svg viewBox="0 0 16 16" style={{ width: 17, height: 17, color: '#fff' }}>
              <path
                d="M8 1.8l5.2 2v4.4c0 3.1-2.1 5.8-5.2 6.9-3.1-1.1-5.2-3.8-5.2-6.9V3.8z"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.4}
                strokeLinejoin="round"
              />
              <path
                d="M5.6 8.1l1.8 1.8 3.2-3.6"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Maintenance Readiness</span>
            <span
              style={{
                fontSize: 10.5,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--mu)',
              }}
            >
              Actimage Delivery
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <h1 style={{ margin: 0, font: '600 24px/1.2 var(--font-sans)', letterSpacing: '-0.015em' }}>
            Sign in
          </h1>
          <p style={NOTE}>
            Your role decides what you can change: auditors record states, delivery managers
            approve, everyone else reads.
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            // A sign-in failure surfaces here, not in the console: a redirect that comes
            // straight back with nothing on screen is the worst version of this.
            void signIn().catch((error: unknown) => {
              setProblem(describeAuthError(error));
              setPhase('failed');
            });
          }}
          style={{
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 9,
            borderRadius: 7,
            border: '1px solid var(--ac)',
            background: 'var(--ac)',
            color: '#fff',
            font: '600 13.5px/1 var(--font-sans)',
            cursor: 'pointer',
          }}
        >
          <svg viewBox="0 0 16 16" style={{ width: 15, height: 15 }}>
            <rect x="1.5" y="1.5" width="5.6" height="5.6" fill="currentColor" opacity={0.95} />
            <rect x="8.9" y="1.5" width="5.6" height="5.6" fill="currentColor" opacity={0.7} />
            <rect x="1.5" y="8.9" width="5.6" height="5.6" fill="currentColor" opacity={0.7} />
            <rect x="8.9" y="8.9" width="5.6" height="5.6" fill="currentColor" opacity={0.5} />
          </svg>
          Continue with your work account
        </button>

        <span style={{ ...NOTE, textAlign: 'center' }}>
          Single sign-on · Microsoft Entra ID · <code>actimage.de</code>
        </span>

        <p style={{ ...NOTE, borderTop: '1px solid var(--bd)', paddingTop: 14 }}>
          Client stakeholders receive a read-only link to a single project&rsquo;s report — they
          do not need an account here.
        </p>
      </div>
    </div>
  );
}
